import React, { useEffect, useState, useCallback } from "react";
import { Button, Card, Statistic, Row, Col, Table, Modal, Form, Input, InputNumber, message, Space, Tag } from "antd";
import {
  TrophyOutlined,
  UserOutlined,
  CalendarOutlined,
  PlayCircleOutlined,
  CheckCircleOutlined
} from "@ant-design/icons";
import { ColumnsType } from "antd/es/table";
import {
  getLotteryStatusApi,
  getLotteryStatisticsApi,
  performLotteryRolloutApi,
  selectLotteryWinnerApi,
  selectWinnersApi
} from "api/lottery";
import { useNotificationContext } from "contexts/NotificationContext";
import { ILotteryStatus, ILotteryWinner, ILotteryStatistics } from "models";

const { confirm } = Modal;

export default function LotteryManagement() {
  const { showError } = useNotificationContext();
  const [loading, setLoading] = useState(false);
  const [statusLoading, setStatusLoading] = useState(false);
  const [statsLoading, setStatsLoading] = useState(false);
  const [lotteryStatus, setLotteryStatus] = useState<ILotteryStatus | null>(null);
  const [lotteryStats, setLotteryStats] = useState<ILotteryStatistics | null>(null);
  const [recentWinners, setRecentWinners] = useState<ILotteryWinner[]>([]);
  const [winnerModalVisible, setWinnerModalVisible] = useState(false);
  const [selectedLotteryNumber, setSelectedLotteryNumber] = useState("");
  const [form] = Form.useForm();

  const fetchLotteryStatus = useCallback(async () => {
    setStatusLoading(true);
    try {
      const { data, error } = await getLotteryStatusApi();
      if (data) {
        setLotteryStatus(data);
      } else {
        showError(error?.message || "Failed to fetch lottery status");
      }
    } catch (err) {
      showError("Failed to fetch lottery status");
    } finally {
      setStatusLoading(false);
    }
  }, [showError]);

  const fetchLotteryStatistics = useCallback(async () => {
    setStatsLoading(true);
    try {
      const { data, error } = await getLotteryStatisticsApi();
      if (data) {
        setLotteryStats(data);
        if (data.recent_winners) {
          setRecentWinners(data.recent_winners);
        }
      } else {
        showError(error?.message || "Failed to fetch lottery statistics");
      }
    } catch (err) {
      showError("Failed to fetch lottery statistics");
    } finally {
      setStatsLoading(false);
    }
  }, [showError]);

  const fetchData = useCallback(async () => {
    await Promise.all([fetchLotteryStatus(), fetchLotteryStatistics()]);
  }, [fetchLotteryStatus, fetchLotteryStatistics]);

  const handleLotteryRollout = () => {
    confirm({
      title: "Perform Lottery Rollout",
      content: "This will select winners for the current month. Continue?",
      onOk: async () => {
        setLoading(true);
        try {
          const { data, error } = await performLotteryRolloutApi();
          if (data) {
            message.success(`Lottery rollout completed! ${data.winners_selected} winners selected.`);
            setRecentWinners(data.winners);
            fetchData();
          } else {
            showError(error?.message || "Failed to perform lottery rollout");
          }
        } catch (err) {
          showError("Failed to perform lottery rollout");
        } finally {
          setLoading(false);
        }
      },
    });
  };

  const handleSelectWinnersBulk = () => {
    confirm({
      title: "Select Lottery Winners",
      content: "This will select 3 winners without sending notifications. Continue?",
      onOk: async () => {
        setLoading(true);
        try {
          const { data, error } = await selectWinnersApi(3);
          if (data) {
            message.success(`Selected ${data.winners_selected} winners!`);
            fetchData();
          } else {
            showError(error?.message || "Failed to select winners");
          }
        } catch (err) {
          showError("Failed to select winners");
        } finally {
          setLoading(false);
        }
      },
    });
  };

  const handleSelectWinner = () => {
    setWinnerModalVisible(true);
  };

  const handleWinnerModalOk = async () => {
    try {
      const values = await form.validateFields();
      const { data, error } = await selectLotteryWinnerApi(
        selectedLotteryNumber,
        values.prize_amount
      );

      if (data) {
        message.success("Winner selected successfully!");
        setWinnerModalVisible(false);
        setSelectedLotteryNumber("");
        form.resetFields();
        fetchLotteryStatus();
      } else {
        showError(error?.message || "Failed to select winner");
      }
    } catch (err) {
      showError("Failed to select winner");
    }
  };

  const winnerColumns: ColumnsType<ILotteryWinner> = [
    {
      title: "Lottery Number",
      dataIndex: "lottery_number",
      key: "lottery_number",
      render: (number: string) => (
        <Tag color="gold" style={{ fontSize: '14px', padding: '4px 8px' }}>
          {number}
        </Tag>
      ),
    },
    {
      title: "Trip ID",
      dataIndex: "trip_id",
      key: "trip_id",
    },
    {
      title: "Owner",
      dataIndex: "owner_type",
      key: "owner_type",
      render: (type: string) => (
        <Tag color={type === "passenger" ? "blue" : type === "driver" ? "green" : "orange"}>
          {type?.toUpperCase() || "SHARED"}
        </Tag>
      ),
    },
    {
      title: "Driver Name",
      dataIndex: "driver_name",
      key: "driver_name",
      render: (name: string) => <span className="font-semibold">{name}</span>
    },
    {
      title: "Passenger Name",
      dataIndex: "passenger_name",
      key: "passenger_name",
      render: (name: string) => <span className="font-semibold">{name}</span>
    },
    {
      title: "Passenger Phone",
      dataIndex: "passenger_phone",
      key: "passenger_phone",
    },
    {
      title: "Driver Phone",
      dataIndex: "driver_phone",
      key: "driver_phone",
    },
    {
      title: "Trip Date",
      dataIndex: "trip_date",
      key: "trip_date",
      render: (date: string) => new Date(date).toLocaleDateString(),
    },
    {
      title: "Prize Amount",
      dataIndex: "prize_amount",
      key: "prize_amount",
      render: (amount: number) => `${amount.toLocaleString()} Birr`,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status: string) => (
        <Tag color={status === "claimed" ? "green" : "orange"}>
          {status || "pending"}
        </Tag>
      ),
    },
  ];

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return (
    <div className="w-full p-6">
      <div className="flex justify-between items-center mb-6">
        <Space align="center">
          <h1 className="text-2xl font-bold">Lottery Management</h1>
          {lotteryStatus && (
            <Space>
              <Tag color={lotteryStatus.is_lottery_day ? "green" : "red"}>
                Lottery Status: {lotteryStatus.is_lottery_day ? "Active" : "Inactive"}
              </Tag>
              {lotteryStatus.lottery_rollout_days && (
                <Tag color="blue">
                  Rollout Days: {lotteryStatus.lottery_rollout_days.join(", ")}
                </Tag>
              )}
              {lotteryStatus.current_day && (
                <Tag color="purple">
                  Today: Day {lotteryStatus.current_day}
                </Tag>
              )}
            </Space>
          )}
        </Space>

        <Space>
          <Button
            type="primary"
            icon={<PlayCircleOutlined />}
            onClick={handleLotteryRollout}
            loading={loading}
            disabled={!lotteryStatus?.is_lottery_day}
          >
            Perform Monthly Rollout
          </Button>

          <Button
            icon={<TrophyOutlined />}
            onClick={handleSelectWinnersBulk}
            loading={loading}
          >
            Select Winner
          </Button>
          <Button
            onClick={fetchData}
            loading={statusLoading || statsLoading}
          >
            Refresh Status
          </Button>
        </Space>
      </div>

      {/* Statistics Cards */}
      <Row gutter={16} className="mb-6">
        <Col span={6}>
          <Card>
            <Statistic
              title="Total Participants"
              value={lotteryStats?.total_participants || 0}
              prefix={<UserOutlined />}
              valueStyle={{ color: '#1890ff' }}
              loading={statsLoading}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Total Winners"
              value={lotteryStats?.total_winners || 0}
              prefix={<TrophyOutlined />}
              valueStyle={{ color: '#52c41a' }}
              loading={statsLoading}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="This Month Participants"
              value={lotteryStats?.this_month_participants || 0}
              prefix={<CalendarOutlined />}
              valueStyle={{ color: '#faad14' }}
              loading={statsLoading}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="This Month Winners"
              value={lotteryStats?.this_month_winners || 0}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: '#f5222d' }}
              loading={statsLoading}
            />
          </Card>
        </Col>
      </Row>

      {/* Next Rollout Info */}
      <Card className="mb-6">
        <Row gutter={16}>
          <Col span={12}>
            <Statistic
              title="Next Rollout Date"
              value={lotteryStats?.next_rollout_date ?
                new Date(lotteryStats.next_rollout_date).toLocaleDateString() :
                "Not scheduled"
              }
              prefix={<CalendarOutlined />}
              loading={statsLoading}
            />
          </Col>
          <Col span={12}>
            <Statistic
              title="Last Rollout Date"
              value={lotteryStats?.last_rollout_date ?
                new Date(lotteryStats.last_rollout_date).toLocaleDateString() :
                "Never"
              }
              prefix={<CalendarOutlined />}
              loading={statsLoading}
            />
          </Col>
        </Row>
      </Card>


      {/* Recent Winners Table */}
      {recentWinners.length > 0 && (
        <Card title="Recent Winners" className="mb-6">
          <Table
            columns={winnerColumns}
            dataSource={recentWinners}
            rowKey="lottery_number"
            pagination={false}
            size="small"
          />
        </Card>
      )}

      {/* Winner Selection Modal */}
      <Modal
        title="Select Lottery Winner"
        open={winnerModalVisible}
        onOk={handleWinnerModalOk}
        onCancel={() => {
          setWinnerModalVisible(false);
          setSelectedLotteryNumber("");
          form.resetFields();
        }}
        okText="Select Winner"
      >
        <Form form={form} layout="vertical">
          <Form.Item
            label="Lottery Number (e.g. P-ABC12345)"
            name="lottery_number"
            rules={[{ required: true, message: "Please enter lottery number" }]}
          >
            <Input
              placeholder="Enter lottery number"
              onChange={(e) => setSelectedLotteryNumber(e.target.value)}
            />
          </Form.Item>
          <Form.Item
            label="Prize Amount (Birr)"
            name="prize_amount"
            rules={[{ required: true, message: "Please enter prize amount" }]}
          >
            <InputNumber
              style={{ width: '100%' }}
              placeholder="Enter prize amount"
              min={0}
              step={10}
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
